import { logger } from '@vagabondinternet/logging';
import { RETURN_TO_PARAM } from '@words-happen/app-urls';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { browserIdentitySession, type IdentitySession, sessionFailure } from '../../api';
import { MessageScreen } from '../../components';
import { type BrowserNavigation, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';

export interface SignOutPageProps {
  session?: IdentitySession;
  navigation?: BrowserNavigation;
}

/** Runs `step`, logging a failure instead of stopping: signing out always completes. */
const attempt = async (message: string, step: () => Promise<void>) => {
  try {
    await step();
  } catch (error) {
    logger.warn({ message, failure: sessionFailure(error) });
  }
};

/**
 * /sign-out: signs out of Firebase and clears the words-happen session (POST /session/sign-out), then shows sign-in
 * carrying the validated return_to, or the game app's home when it is missing or not allowed. Firebase goes first, so
 * the sign-in screen can't silently sign the player straight back in.
 */
export const SignOutPage: React.FC<SignOutPageProps> = ({ session = browserIdentitySession, navigation }) => {
  const navigate = useNavigate();
  const { returnUrl } = useAuthNavigation(navigation);
  // React StrictMode mounts twice in development; sign out once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const signOut = async () => {
      if (session.mayBeSignedIn()) {
        await attempt('Signing out of Firebase failed', session.signOut);
      }
      await attempt('Clearing the session failed', session.endSession);
      navigate(
        { pathname: ROUTES.signIn, search: `?${new URLSearchParams({ [RETURN_TO_PARAM]: returnUrl })}` },
        { replace: true },
      );
    };
    void signOut();
  }, [session, navigate, returnUrl]);

  return (
    <MessageScreen title="Signing you out…" mood="sleepy">
      <p role="status">See you soon.</p>
    </MessageScreen>
  );
};
